import { Link, useParams } from 'react-router-dom';

import { DEFAULT_COUNTRY_CODE } from '@lib/constants/country-code';

export interface AppLinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string;
  children?: React.ReactNode;
}

/**
 * 統一的連結元件，處理 SPA 內部導航和外部連結。
 *
 * - 外部 URL（http/https 開頭）使用 `<a>` 標籤
 * - 所有內部路徑使用 React Router `<Link>` 進行 SPA 導航
 *
 * 內部路徑的 country code 前綴規則：
 * - `/` 開頭的絕對路徑會自動加上當前 country code 前綴（如 `/store` → `/tw/store`）
 * - 非 `/` 開頭的相對路徑會直接傳遞，不加前綴
 */
export default function AppLink({
  href,
  children,
  onClick,
  ...props
}: AppLinkProps) {
  const { countryCode } = useParams<{ countryCode: string }>();
  const code = countryCode || DEFAULT_COUNTRY_CODE;

  // 外部 URL 使用 anchor
  if (href.startsWith('http://') || href.startsWith('https://')) {
    return (
      <a href={href} onClick={onClick} {...props}>
        {children}
      </a>
    );
  }

  // 所有內部路徑使用 React Router Link
  const localizedHref = href.startsWith('/') ? `/${code}${href}` : href;
  return (
    <Link to={localizedHref} onClick={onClick} {...props}>
      {children}
    </Link>
  );
}
